<script setup>
import { ref, defineProps, defineEmits } from 'vue'

const props = defineProps({
  show: Boolean,
  shortcuts: Object,
})

const emit = defineEmits(['close', 'update'])

const editingShortcut = ref(null)
const localShortcuts = ref({ ...props.shortcuts })

const startRecording = (shortcutKey) => {
  editingShortcut.value = shortcutKey
}

const stopRecording = () => {
  editingShortcut.value = null
}

const handleKeyDown = (event, shortcutKey) => {
  if (!editingShortcut.value) return

  event.preventDefault()
  event.stopPropagation()
  
  // Build shortcut string
  let shortcut = []
  if (event.metaKey) shortcut.push('⌘')
  if (event.altKey) shortcut.push('⌥')
  if (event.shiftKey) shortcut.push('⇧')
  
  // Handle special keys
  if (event.key.length === 1) {
    shortcut.push(event.key.toUpperCase())
  } else if (!['Meta', 'Alt', 'Shift'].includes(event.key)) {
    shortcut.push(event.key)
  }

  if (shortcut.length > 1) { // Must have at least one modifier
    localShortcuts.value[shortcutKey] = shortcut.join(' ')
    stopRecording()
    emit('update', { ...localShortcuts.value })
  }
}

const resetToDefault = (shortcutKey) => {
  localShortcuts.value[shortcutKey] = props.shortcuts[shortcutKey]
  emit('update', { ...localShortcuts.value })
}

const categories = {
  'Navigation': ['Projections Tab', 'Balanced Lines Tab', 'Milestones Tab', 'Settlement Tab'],
  'Markets': ['Publish Odds', 'Reset Markets', 'Create Markets'],
  'Toggles': ['Toggle True Probability', 'Toggle Odds', 'Toggle Logs', 'Toggle Scraped Settlement', 'Toggle Shortcuts Menu']
}
</script>

<template>
  <div v-if="show" class="fixed inset-0 z-50 overflow-y-auto">
    <div class="flex items-center justify-center min-h-screen p-4">
      <!-- Backdrop -->
      <div class="fixed inset-0 bg-black bg-opacity-50" @click="$emit('close')"></div>

      <!-- Modal -->
      <div class="relative bg-white rounded-lg shadow-xl max-w-2xl w-full">
        <!-- Header -->
        <div class="flex items-center justify-between p-4 border-b">
          <div class="flex items-center gap-3">
            <i class="fas fa-keyboard text-gray-400"></i>
            <h3 class="text-lg font-semibold">Keyboard Shortcuts</h3>
          </div>
          <button @click="$emit('close')" class="text-gray-400 hover:text-gray-600">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <!-- Content -->
        <div class="p-6">
          <!-- Categories -->
          <div v-for="(shortcuts, category) in categories" :key="category" class="mb-6">
            <h4 class="text-sm font-medium text-gray-500 mb-3">{{ category }}</h4>
            
            <div class="space-y-2">
              <div v-for="shortcutKey in shortcuts" 
                   :key="shortcutKey"
                   class="flex items-center justify-between p-3 rounded-lg border"
                   :class="{ 'bg-blue-50 border-blue-200': editingShortcut === shortcutKey }">
                <div class="flex-1">
                  <div class="font-medium">{{ shortcutKey }}</div>
                  <div class="text-sm text-gray-500">
                    {{ editingShortcut === shortcutKey ? 'Press your new shortcut...' : 'Click to edit shortcut' }}
                  </div>
                </div>

                <div class="flex items-center gap-3">
                  <button @click="resetToDefault(shortcutKey)"
                          class="text-sm text-gray-400 hover:text-gray-600"
                          title="Reset to default">
                    <i class="fas fa-undo"></i>
                  </button>

                  <button v-if="editingShortcut === shortcutKey"
                          @click="stopRecording"
                          class="px-3 py-1 text-sm bg-gray-100 rounded">
                    Cancel
                  </button>

                  <button @click="editingShortcut === shortcutKey ? stopRecording() : startRecording(shortcutKey)"
                          @keydown="(e) => handleKeyDown(e, shortcutKey)"
                          class="min-w-[120px] px-3 py-1.5 text-sm bg-gray-100 rounded font-mono text-center"
                          :class="{ 'bg-blue-100': editingShortcut === shortcutKey }">
                    {{ localShortcuts[shortcutKey] }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Tips -->
          <div class="mt-6 p-4 bg-gray-50 rounded-lg">
            <div class="text-sm font-medium text-gray-700 mb-2">Tips:</div>
            <ul class="text-sm text-gray-600 space-y-1">
              <li class="flex items-center gap-2">
                <i class="fas fa-info-circle text-blue-500"></i>
                Click on any shortcut to edit
              </li>
              <li class="flex items-center gap-2">
                <i class="fas fa-info-circle text-blue-500"></i>
                Shortcuts must include at least one modifier key (⌘, ⌥, or ⇧)
              </li>
              <li class="flex items-center gap-2">
                <i class="fas fa-info-circle text-blue-500"></i>
                Click the reset icon to restore default shortcut
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.shortcut-button {
  @apply min-w-[120px] px-3 py-1.5 text-sm bg-gray-100 rounded font-mono text-center;
}

.shortcut-button-recording {
  @apply bg-blue-100;
}

.shortcut-row {
  @apply flex items-center justify-between p-3 rounded-lg border;
}

.shortcut-row-recording {
  @apply bg-blue-50 border-blue-200;
}

.reset-button {
  @apply text-sm text-gray-400 hover:text-gray-600;
}

.cancel-button {
  @apply px-3 py-1 text-sm bg-gray-100 rounded;
}
</style> 